import type { Geo } from '@/api/types/ProviderApi'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { CHOICE_HOLD_MS } from '@/constants/timing'
import { ChoiceRow } from '@/ui/core/choice'
import { Pressable } from '@/ui/core/pressable'
import { Sheet, SheetTitle } from '@/ui/core/sheet'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useEffect, useRef, useState } from 'react'
import { View } from 'react-native'

/**
 * Which city, as a line under the heading.
 *
 * The name itself is the control, underlined with a caret after it. "New York · change" spent
 * two thirds of the line on the verb and made the city a label beside a button, when the city is
 * the thing being pressed — and the studio cards below name a place and open it on the same
 * gesture, so the line that names a place should work the same way.
 *
 * The server already answers with the member's home city, so this states a fact and offers to
 * change it rather than asking a question. It has been a segmented row and a bordered field,
 * and both spent the top of the screen on the least-used decision on it — the one thing here
 * worth a heading is the week, not the city.
 *
 * Hidden entirely when there is only one, which is the common case: a control with a single
 * option is a question nobody was asking.
 *
 * A tap fills the row it landed on and holds it there for a beat before the sheet goes. Closing
 * on the touch itself meant the mark never drew: the sheet was gone before the row could show
 * it had been chosen, so the only evidence a tap had registered was the city name changing
 * behind it — and a member who had just mis-tapped could not tell which row they had hit.
 */
export const GeoSwitcher = ({
	geos,
	selectedGeoId,
	onSelect,
}: { geos: Geo[]; selectedGeoId: number; onSelect: (geoId: number) => void }) => {
	const [isOpen, setIsOpen] = useState(false)
	// The row the finger landed on, held while the mark draws. Falls back to the real answer,
	// which is what the sheet shows every other time it is opened.
	const [pending, setPending] = useState<number>()
	const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
	const current = geos.find((geo) => geo.id === selectedGeoId)

	// A sheet dismissed by the backdrop, or a screen left mid-beat, must not fire the choice.
	useEffect(() => () => clearTimeout(timer.current), [])

	if (geos.length < 2) {
		return null
	}

	const choose = (geoId: number) => {
		if (timer.current) {
			return
		}
		setPending(geoId)
		timer.current = setTimeout(() => {
			timer.current = undefined
			setPending(undefined)
			setIsOpen(false)
			onSelect(geoId)
		}, CHOICE_HOLD_MS)
	}

	return (
		<>
			<Pressable
				onPress={() => setIsOpen(true)}
				accessibilityRole="button"
				accessibilityLabel={`${current?.name ?? 'No city'}. Change city`}
				accessibilityState={{ expanded: isOpen }}
				hitSlop={10}
				/* Only as wide as the city it names. Left to fill the row, the press target ran
				   the width of the screen, so a tap anywhere on an empty line opened the city
				   sheet — and the rule under the name was the only clue where the control
				   actually was. */
				className="self-start"
			>
				{/* Two points in. The heading above is PP Right Grotesk, whose S carries about a
				    point of left side bearing that Maison Neue's N does not, so two lines sharing
				    one layout edge do not share an optical one — and the underline, which starts
				    at the box rather than at the letter, pulls the eye further left again. */}
				{/* The rule is drawn under the pair rather than under the word. Text underline stops
				    where the text does, which left the caret hanging past the end of the line it
				    belonged to; a border on the row carries under both, and the whole thing reads
				    as one control.
				    
				    A point in from the left. The heading above is PP Right Grotesk, whose S carries
				    about a point of left side bearing that Maison Neue's N does not, so two lines
				    sharing one layout edge do not share an optical one. */}
				<HStack
					className="items-center self-start pb-0.5 pl-px"
					gap={4}
					style={{ borderBottomWidth: 1, borderBottomColor: colors.ink }}
				>
					<Text size="body2" weight="bold" numberOfLines={1}>
						{current?.name ?? 'Choose a city'}
					</Text>
					{/* Turned down, not right: this unfolds a list in place rather than sending the
					    member onward, and down is what every disclosure in this app points. */}
					<Glyph name="chevron" size={11} color={colors.ink} rotate={90} />
				</HStack>
			</Pressable>

			<Sheet
				open={isOpen}
				onClose={() => {
					clearTimeout(timer.current)
					timer.current = undefined
					setPending(undefined)
					setIsOpen(false)
				}}
			>
				<View className="pt-2">
					<SheetTitle glyph="pin">Choose a City</SheetTitle>
					<VStack gap={10} className="px-4 pb-8">
						{geos.map((geo) => {
							const checked = geo.id === (pending ?? selectedGeoId)
							return (
								<ChoiceRow
									key={geo.id}
									checked={checked}
									multi={false}
									accessibilityLabel={geo.name}
									onPress={() => choose(geo.id)}
								>
									{/* The row draws its own mark and fills when it is the chosen one, so
								    the label has to turn with it. */}
									<Text weight="bold" tone={checked ? 'white' : 'ink'} className="flex-1">
										{geo.name}
									</Text>
								</ChoiceRow>
							)
						})}
					</VStack>
				</View>
			</Sheet>
		</>
	)
}
